/* eslint-disable react/forbid-component-props */
import { faCompass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useContext } from 'react';
import { useMapEvents, useMap } from 'react-leaflet';
import { MAP_VIEW_CONFIG } from '../../brands/default/config';
import ThemeContext from '../../providers/ThemeProvider';
import Message from '../_generic/Message';
import PrimaryButton from '../_generic/button/PrimaryButton';

const SearchThisArea = ({
  isLoading,
  isMissingFilter,
  isNoResults,
  isSlowApi,
  selectedCategories,
  sliderValues,
  updateBounds,
  updateStart,
  zoomEnd,
}) => {
  // const isSlowApi = true;
  const map = useMap();
  const theme = useContext(ThemeContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateBoundsDebounced = useCallback(
    debounce((bounds, newSelectedCategories) => {
      // waiting for 1000ms after map event before doing a call
      // so many small scroll movements don't all make api calls
      updateBounds(bounds, newSelectedCategories);
    }, MAP_VIEW_CONFIG.MAP_API_DEBOUNCE_TIMEOUT),
    [],
  );

  const handleMapEvent = (bounds, newSelectedCategories) => {
    // make user click the button if api is slow
    if (isSlowApi) return;
    updateBoundsDebounced(bounds, newSelectedCategories);
  };

  const handleButtonClick = () => {
    updateBounds(map.getBounds(), selectedCategories);
  };

  // noinspection JSUnusedGlobalSymbols
  const mapEvents = useMapEvents({
    dragend: (event) => {
      if (!isLoading && event.distance > MAP_VIEW_CONFIG.MAP_DRAG_BEFORE_UPDATE)
        handleMapEvent(mapEvents.getBounds(), {
          categories: selectedCategories,
          sliderValues,
        });
    },
    dragstart: () => {
      updateStart();
    },
    moveend: () => {
      // fires too often, can't be used
    },
    zoomanim: () => {
      // console.log('zoomanim');
    },
    zoomend: () => {
      // eslint-disable-next-line no-negated-condition
      if (!isLoading) {
        handleMapEvent(mapEvents.getBounds(), {
          categories: selectedCategories,
          sliderValues,
        });
      } else {
        // need to handle auto zoomin on first load for highlight of map pin
        zoomEnd();
      }
    },
    zoomstart: () => {
      updateStart();
    },
  });

  return (
    <>
      {isSlowApi && (
        <div className="button-container item-container">
          <PrimaryButton
            className="font-weight-normal full-width opacity_seventy primary-button uppercase"
            onClick={() => {
              handleButtonClick();
            }}
          >
            Search this area
            <span className="spinner spinner-button">
              <FontAwesomeIcon icon={faCompass} spin={isLoading} />
            </span>
          </PrimaryButton>
        </div>
      )}
      {!isSlowApi && isLoading && (
        <div className="spinner-container item-container">
          <PrimaryButton className="font-weight-normal full-width opacity_seventy primary-button uppercase grey-color">
            <span className="spinner">
              <FontAwesomeIcon icon={faCompass} spin={isLoading} />
            </span>
          </PrimaryButton>
        </div>
      )}
      {isNoResults && !isLoading && (
        <div className="item-container message-wrapper">
          {isMissingFilter ? (
            <Message
              iconPosition="left"
              message="No deals with the chosen categories in this area."
              zIndex={0}
            />
          ) : (
            <Message
              iconPosition="left"
              message="No deals here. Zoom out or reset your filters to view more deals."
              zIndex={0}
            />
          )}
        </div>
      )}

      <style jsx>{`
        .item-container {
          position: absolute;
          top: 5.5rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 500;
        }
        .message-wrapper {
          font-size: 13px;
        }
        .button-container {
          width: 25rem;
        }
        .spinner-container {
          width: 5rem;
        }
        .spinner {
          font-size: 25px;
        }
        .spinner-button {
          margin-left: 10px;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .button-container {
            top: 2rem;
          }
          .spinner-container {
            top: 2rem;
          }
        }
      `}</style>
    </>
  );
};

SearchThisArea.propTypes = {
  isLoading: PropTypes.bool,
  isMissingFilter: PropTypes.bool,
  isNoResults: PropTypes.bool,
  isSlowApi: PropTypes.bool,
  selectedCategories: PropTypes.array,
  sliderValues: PropTypes.array,
  updateBounds: PropTypes.func,
  updateStart: PropTypes.func,
  zoomEnd: PropTypes.func,
};

export default SearchThisArea;
